import {
  useMemo,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Controller,
  useForm,
  type Control,
  type SubmitHandler,
  type UseFormRegisterReturn,
} from "react-hook-form";
import { z } from "zod";
import { TkticSelect } from "@/components/ui/TkticSelect";
import {
  ApiError,
  getAdminAuthProvidersSettings,
  getAdminEmailSettings,
  getAdminSettings,
  sendAdminEmailTest,
  updateAdminAuthProvidersSettings,
  updateAdminEmailSettings,
  updateAdminSettings,
  uploadAdminSettingsAsset,
} from "@/lib/api";
import { useToast } from "@/components/ui/toast";
import { unscopedKey } from "@/lib/queryClient";
import { coerceBool } from "@/lib/coerce";
import type {
  AdminAuthProvidersPayload,
  AdminEmailPayload,
  AdminSettingsPayload,
} from "@/lib/types";
import { AdminPageHeader } from "../components/AdminPageHeader";

/**
 * M8c H1 / H3 / H4 / H5 / H6 / H7 — `/admin/settings`.
 *
 * Club identity (title + intro) + Downloads toggle + Asset uploads (logo /
 * favicon) + Email/Notification card (editable provider/base URL/from
 * fields/write-only Brevo key + test-send). Audit feed reads the same
 * payload's `audit` field.
 *
 * **H2 build-time decision (locked):** the three live-streaming
 * fields (`live_enabled`, `live_rtmp_public_url`,
 * `live_offline_message`) are NOT rendered here. A one-line
 * pointer directs operators to `/admin/live`. This avoids the
 * legacy bug where a partial Settings save could clobber Live
 * config — and removes a confusing dual-edit surface.
 *
 * Save flow (H6): partial PUT — only changed keys go to the
 * backend. The backend echoes the updated settings + an audit
 * array. We invalidate to refetch + the audit feed updates
 * automatically.
 */

/** Operator-facing club identity fields. */
const BRANDING_FIELDS: Array<{
  key: string;
  label: string;
  group: "club";
  kind?: "text" | "textarea";
}> = [
  { key: "season_title", label: "Club title", group: "club" },
  { key: "season_intro", label: "Club intro", group: "club", kind: "textarea" },
];

const BRANDING_GROUP_LABELS: Record<
  (typeof BRANDING_FIELDS)[number]["group"],
  string
> = {
  club: "Club",
};

const emailSettingsSchema = z.object({
  email_provider: z.enum(["disabled", "brevo"]),
  email_public_base_url: z.string(),
  email_from: z.string().email("Enter a valid sender email").or(z.literal("")),
  email_from_name: z.string(),
  email_brevo_api_key: z.string(),
  clear_brevo_api_key: z.boolean(),
});

type EmailSettingsValues = z.infer<typeof emailSettingsSchema>;

const emailTestSchema = z.object({
  email: z.string().email("Enter a valid recipient email"),
});

type EmailTestValues = z.infer<typeof emailTestSchema>;

const authProviderSettingsSchema = z.object({
  auth_google_client_id: z.string(),
  auth_google_client_secret: z.string(),
  clear_google_client_secret: z.boolean(),
  auth_apple_client_id: z.string(),
  auth_apple_team_id: z.string(),
  auth_apple_key_id: z.string(),
  auth_apple_private_key: z.string(),
  clear_apple_private_key: z.boolean(),
});

type AuthProviderSettingsValues = z.infer<typeof authProviderSettingsSchema>;

const SETTINGS_SECTIONS = {
  brand: {
    id: "settings-brand",
    testId: "admin-settings-section-brand",
    label: "Brand",
    title: "Brand & public experience",
    description:
      "Public club copy, downloads, uploadable assets, and live-stream handoff.",
  },
  notifications: {
    id: "settings-notifications",
    testId: "admin-settings-section-notifications",
    label: "Notifications",
    title: "Notifications",
    description:
      "Transactional delivery provider, sender identity, and delivery tests.",
  },
  onboarding: {
    id: "settings-onboarding",
    testId: "admin-settings-section-onboarding",
    label: "Onboarding",
    title: "Onboarding & access",
    description:
      "Public account creation, team-access requests, and social sign-in gates.",
  },
  security: {
    id: "settings-security",
    testId: "admin-settings-section-security",
    label: "Security",
    title: "Security & identity",
    description:
      "Private Google, Apple ID, and Kratos provider credential state.",
  },
  audit: {
    id: "settings-audit",
    testId: "admin-settings-section-audit",
    label: "Audit",
    title: "Audit trail",
    description: "Recent operator changes and platform traceability.",
  },
} as const;

type SettingsSectionDefinition =
  (typeof SETTINGS_SECTIONS)[keyof typeof SETTINGS_SECTIONS];

const SETTINGS_SECTION_LIST: SettingsSectionDefinition[] =
  Object.values(SETTINGS_SECTIONS);

export function AdminSettingsPage() {
  const settingsQuery = useQuery({
    queryKey: unscopedKey("admin-settings"),
    queryFn: getAdminSettings,
  });

  return (
    <div data-testid="admin-settings" className="flex flex-col gap-3">
      <AdminPageHeader
        eyebrow="Platform"
        title="Settings"
        description="Manage platform identity, notifications, onboarding policy, identity providers, and audit context."
        meta={settingsQuery.isFetching ? "Refreshing…" : undefined}
      />

      {settingsQuery.error ? (
        <p
          data-testid="admin-settings-error"
          className="text-destructive font-mono text-xs"
        >
          Failed to load settings.
        </p>
      ) : null}

      {settingsQuery.data ? (
        <div className="flex flex-col gap-5">
          <SettingsDirectory />

          <SettingsSection
            section={SETTINGS_SECTIONS.brand}
            contentClassName="grid gap-3 xl:grid-cols-2"
          >
            <div className="xl:col-span-2">
              <LiveSettingsPointer />
            </div>
            <BrandingCard settings={settingsQuery.data} />
            <DownloadsCard settings={settingsQuery.data} />
            <div className="xl:col-span-2">
              <AssetsCard settings={settingsQuery.data} />
            </div>
          </SettingsSection>

          <SettingsSection section={SETTINGS_SECTIONS.notifications}>
            <EmailCard />
          </SettingsSection>

          <SettingsSection section={SETTINGS_SECTIONS.onboarding}>
            <OnboardingCard settings={settingsQuery.data} />
          </SettingsSection>

          <SettingsSection section={SETTINGS_SECTIONS.security}>
            <AuthProvidersCard />
          </SettingsSection>

          <SettingsSection section={SETTINGS_SECTIONS.audit}>
            <AuditCard audit={settingsQuery.data.audit ?? []} />
          </SettingsSection>
        </div>
      ) : null}
    </div>
  );
}

function SettingsDirectory() {
  return (
    <nav
      data-testid="admin-settings-directory"
      aria-label="Settings sections"
      className="rounded border border-rule bg-surface p-2 shadow-sm"
    >
      <div className="grid gap-2 md:grid-cols-5">
        {SETTINGS_SECTION_LIST.map((section) => (
          <a
            key={section.id}
            href={`#${section.id}`}
            data-testid={`${section.testId}-link`}
            className="rounded border border-rule bg-elevated px-3 py-2 text-left transition-colors hover:bg-field focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <span className="block font-display text-[0.65rem] font-semibold uppercase tracking-wider text-ink">
              {section.label}
            </span>
            <span className="mt-1 block text-[0.62rem] leading-4 text-ink-muted">
              {section.description}
            </span>
          </a>
        ))}
      </div>
    </nav>
  );
}

function SettingsSection({
  section,
  contentClassName = "grid gap-3",
  children,
}: {
  section: SettingsSectionDefinition;
  contentClassName?: string;
  children: ReactNode;
}) {
  return (
    <section
      id={section.id}
      data-testid={section.testId}
      className="scroll-mt-24"
    >
      <header className="mb-2 border-b border-rule pb-2">
        <h2 className="font-display text-sm font-semibold uppercase tracking-wider text-ink">
          {section.title}
        </h2>
        <p className="mt-1 max-w-3xl text-xs leading-5 text-ink-muted">
          {section.description}
        </p>
      </header>
      <div className={contentClassName}>{children}</div>
    </section>
  );
}

function LiveSettingsPointer() {
  return (
    <div
      data-testid="admin-settings-live-pointer"
      className="rounded border border-rule bg-surface shadow-sm p-3 text-xs"
    >
      <p className="text-ink">
        <span className="text-ink-muted">Live config:</span> manage live
        streaming key, RTMP URL, and offline message on{" "}
        <a
          href="/admin/live"
          data-testid="admin-settings-live-link"
          className="text-accent hover:underline"
        >
          /admin/live
        </a>
        .
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Onboarding controls
// ---------------------------------------------------------------------------

function OnboardingCard({ settings }: { settings: AdminSettingsPayload }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const live = (settings.settings ?? {}) as Record<string, unknown>;
  const selfServiceEnabled = coerceBool(live.onboarding_self_service_enabled ?? "1");
  const socialLoginEnabled = coerceBool(live.onboarding_social_login_enabled);
  const googleEnabled = coerceBool(live.onboarding_google_enabled ?? "1");
  const appleEnabled = coerceBool(live.onboarding_apple_enabled ?? "1");
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: (payload: Record<string, unknown>) => updateAdminSettings(payload),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: unscopedKey("admin-settings"),
        }),
        queryClient.invalidateQueries({
          queryKey: unscopedKey("app-settings"),
        }),
      ]);
      setError(null);
      toast.success("Onboarding settings saved");
    },
    onError: (err) => {
      setError(formatErr(err));
      toast.error("Couldn't save onboarding settings", {
        description: formatErr(err),
      });
    },
  });

  const toggle = (key: string, current: boolean) => {
    setError(null);
    mut.mutate({ [key]: current ? "0" : "1" });
  };

  return (
    <section
      data-testid="admin-settings-onboarding"
      className="flex flex-col gap-3 rounded border border-rule bg-surface shadow-sm p-3"
    >
      <header>
        <h3 className="text-sm font-display uppercase tracking-wider text-ink">
          Onboarding
        </h3>
        <p className="mt-1 text-[0.65rem] text-ink-muted">
          Control public registration, access requests, and which configured social providers are offered.
        </p>
      </header>
      <div className="grid gap-2 md:grid-cols-2">
        <OnboardingToggleRow
          title="Self-service onboarding"
          description="Allows new account creation and team access requests."
          enabled={selfServiceEnabled}
          disabled={mut.isPending}
          testId="admin-settings-onboarding-self-service"
          onToggle={() =>
            toggle("onboarding_self_service_enabled", selfServiceEnabled)
          }
        />
        <OnboardingToggleRow
          title="Social sign-in"
          description="Allows Kratos OIDC sessions to exchange into Tktic app sessions."
          enabled={socialLoginEnabled}
          disabled={mut.isPending}
          testId="admin-settings-onboarding-social"
          onToggle={() =>
            toggle("onboarding_social_login_enabled", socialLoginEnabled)
          }
        />
        <OnboardingToggleRow
          title="Google"
          description="Shows the Google option when Kratos returns a configured Google provider."
          enabled={googleEnabled}
          disabled={mut.isPending || !socialLoginEnabled}
          testId="admin-settings-onboarding-google"
          onToggle={() => toggle("onboarding_google_enabled", googleEnabled)}
        />
        <OnboardingToggleRow
          title="Apple ID"
          description="Shows the Apple ID option when Kratos returns a configured Apple provider."
          enabled={appleEnabled}
          disabled={mut.isPending || !socialLoginEnabled}
          testId="admin-settings-onboarding-apple"
          onToggle={() => toggle("onboarding_apple_enabled", appleEnabled)}
        />
      </div>
      {error ? (
        <p
          data-testid="admin-settings-onboarding-error"
          className="text-destructive font-mono text-xs"
        >
          {error}
        </p>
      ) : null}
    </section>
  );
}

function OnboardingToggleRow({
  title,
  description,
  enabled,
  disabled,
  testId,
  onToggle,
}: {
  title: string;
  description: string;
  enabled: boolean;
  disabled?: boolean;
  testId: string;
  onToggle: () => void;
}) {
  return (
    <div
      data-testid={testId}
      className="flex items-start justify-between gap-3 rounded border border-rule bg-elevated p-2"
    >
      <div className="min-w-0">
        <h4 className="font-display text-xs font-semibold uppercase tracking-wider text-ink">
          {title}
        </h4>
        <p className="mt-1 text-[0.65rem] leading-4 text-ink-muted">
          {description}
        </p>
      </div>
      <button
        type="button"
        onClick={onToggle}
        disabled={disabled}
        data-testid={`${testId}-toggle`}
        data-active={enabled ? "true" : undefined}
        className="shrink-0 rounded border px-3 py-1.5 text-[0.65rem] font-display uppercase tracking-wider transition-colors border-rule text-ink-muted hover:text-ink hover:bg-field data-[active=true]:border-accent data-[active=true]:bg-accent data-[active=true]:text-accent-ink disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {enabled ? "On" : "Off"}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Auth provider credentials
// ---------------------------------------------------------------------------

function AuthProvidersCard() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: unscopedKey("admin-auth-providers"),
    queryFn: getAdminAuthProvidersSettings,
  });

  return (
    <section
      data-testid="admin-settings-auth-providers"
      className="flex flex-col gap-3 rounded border border-rule bg-surface shadow-sm p-3"
    >
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h3 className="text-sm font-display uppercase tracking-wider text-ink">
            Auth Providers
          </h3>
          <p className="mt-1 text-[0.65rem] text-ink-muted">
            Store Google and Apple ID credentials privately for Kratos social sign-in.
          </p>
        </div>
      </header>
      {query.isLoading ? (
        <p className="text-[0.6rem] font-mono text-ink-muted">Loading…</p>
      ) : query.error ? (
        <p
          data-testid="admin-settings-auth-providers-error"
          className="text-destructive font-mono text-xs"
        >
          {formatErr(query.error)}
        </p>
      ) : query.data ? (
        <AuthProvidersForm
          payload={query.data}
          onSaved={async () => {
            await queryClient.invalidateQueries({
              queryKey: unscopedKey("admin-auth-providers"),
            });
          }}
        />
      ) : null}
    </section>
  );
}

function authProviderDefaults(
  payload: AdminAuthProvidersPayload,
): AuthProviderSettingsValues {
  return {
    auth_google_client_id: payload.settings.auth_google_client_id ?? "",
    auth_google_client_secret: "",
    clear_google_client_secret: false,
    auth_apple_client_id: payload.settings.auth_apple_client_id ?? "",
    auth_apple_team_id: payload.settings.auth_apple_team_id ?? "",
    auth_apple_key_id: payload.settings.auth_apple_key_id ?? "",
    auth_apple_private_key: "",
    clear_apple_private_key: false,
  };
}

function AuthProvidersForm({
  payload,
  onSaved,
}: {
  payload: AdminAuthProvidersPayload;
  onSaved: () => Promise<void>;
}) {
  const toast = useToast();
  const [submitMessage, setSubmitMessage] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const form = useForm<AuthProviderSettingsValues>({
    resolver: zodResolver(authProviderSettingsSchema),
    values: authProviderDefaults(payload),
  });
  const clearGoogleSecret = form.watch("clear_google_client_secret");
  const clearAppleKey = form.watch("clear_apple_private_key");
  const { settings, status } = payload;

  const save = useMutation({
    mutationFn: updateAdminAuthProvidersSettings,
    onSuccess: async (next) => {
      form.reset(authProviderDefaults(next));
      setSubmitError(null);
      setSubmitMessage("Auth provider settings saved.");
      toast.success("Auth provider settings saved");
      await onSaved();
    },
    onError: (err) => {
      setSubmitMessage(null);
      setSubmitError(formatErr(err));
      toast.error("Couldn't save auth provider settings", {
        description: formatErr(err),
      });
    },
  });

  const onSubmit: SubmitHandler<AuthProviderSettingsValues> = (values) => {
    setSubmitError(null);
    setSubmitMessage(null);
    save.mutate({
      auth_google_client_id: values.auth_google_client_id.trim(),
      ...(values.clear_google_client_secret
        ? { clear_google_client_secret: true }
        : values.auth_google_client_secret.trim()
          ? { auth_google_client_secret: values.auth_google_client_secret.trim() }
          : {}),
      auth_apple_client_id: values.auth_apple_client_id.trim(),
      auth_apple_team_id: values.auth_apple_team_id.trim(),
      auth_apple_key_id: values.auth_apple_key_id.trim(),
      ...(values.clear_apple_private_key
        ? { clear_apple_private_key: true }
        : values.auth_apple_private_key.trim()
          ? { auth_apple_private_key: values.auth_apple_private_key.trim() }
          : {}),
    });
  };

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      data-testid="admin-settings-auth-providers-form"
      className="flex flex-col gap-3 text-xs"
    >
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <SettingsStatusPill
          label="Google"
          value={status.google.configured ? "Ready" : "Needs setup"}
          sub={sourceLabel(status.google.client_secret_source)}
          tone={status.google.configured ? "good" : "warn"}
          testid="admin-settings-auth-google-status"
        />
        <SettingsStatusPill
          label="Apple ID"
          value={status.apple.configured ? "Ready" : "Needs setup"}
          sub={sourceLabel(status.apple.private_key_source)}
          tone={status.apple.configured ? "good" : "warn"}
          testid="admin-settings-auth-apple-status"
        />
        <SettingsStatusPill
          label="Kratos"
          value={status.restart_required ? "Restart after save" : "Live"}
          sub={status.generated_config_exists ? "generated config ready" : "save to generate config"}
          tone={status.restart_required ? "warn" : "good"}
          testid="admin-settings-auth-restart"
        />
        <SettingsStatusPill
          label="Public origin"
          value={status.public_origin || "Not set"}
          tone={status.public_origin ? "muted" : "warn"}
          testid="admin-settings-auth-origin"
        />
      </div>

      <div className="rounded border border-rule bg-elevated">
        <div className="border-b border-rule px-2 py-1">
          <h4 className="font-display text-[0.65rem] font-semibold uppercase tracking-wider text-ink">
            Google
          </h4>
          <p className="mt-0.5 text-[0.6rem] font-mono text-ink-muted">
            Callback: {status.google.callback_url || "Set public origin first"}
          </p>
        </div>
        <div className="grid grid-cols-1 divide-y divide-rule md:grid-cols-2 md:divide-x md:divide-y-0">
          <InlineSettingsField
            label="Client ID"
            current={settings.auth_google_client_id || "Not set"}
            currentTestid="admin-settings-auth-google-client-id"
            testid="admin-settings-auth-google-client-id-input"
            registration={form.register("auth_google_client_id")}
            error={form.formState.errors.auth_google_client_id?.message}
            placeholder="Google OAuth client ID"
            autoComplete="off"
          />
          <InlineSettingsField
            label="New client secret"
            current={
              status.google.has_client_secret
                ? `Configured from ${status.google.client_secret_source || "settings"}`
                : "Not set"
            }
            testid="admin-settings-auth-google-secret-input"
            registration={form.register("auth_google_client_secret")}
            error={form.formState.errors.auth_google_client_secret?.message}
            placeholder={status.google.has_client_secret ? "Leave blank to keep current secret" : "Paste new client secret"}
            type="password"
            autoComplete="new-password"
            disabled={clearGoogleSecret}
          />
        </div>
        <div className="border-t border-rule p-2">
          <button
            type="button"
            onClick={() =>
              form.setValue("clear_google_client_secret", !clearGoogleSecret)
            }
            data-testid="admin-settings-auth-google-clear-secret"
            data-active={clearGoogleSecret ? "true" : undefined}
            className="rounded border px-3 py-2 text-xs font-display uppercase tracking-wider transition-colors border-rule text-ink-muted hover:text-ink hover:bg-field data-[active=true]:border-accent data-[active=true]:bg-accent data-[active=true]:text-accent-ink"
          >
            {clearGoogleSecret ? "Clear Google secret ✓" : "Clear saved Google secret"}
          </button>
        </div>
      </div>

      <div className="rounded border border-rule bg-elevated">
        <div className="border-b border-rule px-2 py-1">
          <h4 className="font-display text-[0.65rem] font-semibold uppercase tracking-wider text-ink">
            Apple ID
          </h4>
          <p className="mt-0.5 text-[0.6rem] font-mono text-ink-muted">
            Callback: {status.apple.callback_url || "Set public origin first"}
          </p>
        </div>
        <div className="grid grid-cols-1 divide-y divide-rule md:grid-cols-2 md:divide-x md:divide-y-0">
          <InlineSettingsField
            label="Services ID"
            current={settings.auth_apple_client_id || "Not set"}
            currentTestid="admin-settings-auth-apple-client-id"
            testid="admin-settings-auth-apple-client-id-input"
            registration={form.register("auth_apple_client_id")}
            error={form.formState.errors.auth_apple_client_id?.message}
            placeholder="com.tktic.web"
            autoComplete="off"
          />
          <InlineSettingsField
            label="Team ID"
            current={settings.auth_apple_team_id || "Not set"}
            currentTestid="admin-settings-auth-apple-team-id"
            testid="admin-settings-auth-apple-team-id-input"
            registration={form.register("auth_apple_team_id")}
            error={form.formState.errors.auth_apple_team_id?.message}
            placeholder="Apple developer team ID"
            autoComplete="off"
          />
        </div>
        <div className="grid grid-cols-1 divide-y divide-rule border-t border-rule md:grid-cols-2 md:divide-x md:divide-y-0">
          <InlineSettingsField
            label="Key ID"
            current={settings.auth_apple_key_id || "Not set"}
            currentTestid="admin-settings-auth-apple-key-id"
            testid="admin-settings-auth-apple-key-id-input"
            registration={form.register("auth_apple_key_id")}
            error={form.formState.errors.auth_apple_key_id?.message}
            placeholder="Apple private key ID"
            autoComplete="off"
          />
          <InlineSettingsTextArea
            label="New private key"
            current={
              status.apple.has_private_key
                ? `Configured from ${status.apple.private_key_source || "settings"}`
                : "Not set"
            }
            testid="admin-settings-auth-apple-private-key-input"
            registration={form.register("auth_apple_private_key")}
            error={form.formState.errors.auth_apple_private_key?.message}
            placeholder={status.apple.has_private_key ? "Leave blank to keep current key" : "Paste Apple private key"}
            disabled={clearAppleKey}
          />
        </div>
        <div className="border-t border-rule p-2">
          <button
            type="button"
            onClick={() => form.setValue("clear_apple_private_key", !clearAppleKey)}
            data-testid="admin-settings-auth-apple-clear-key"
            data-active={clearAppleKey ? "true" : undefined}
            className="rounded border px-3 py-2 text-xs font-display uppercase tracking-wider transition-colors border-rule text-ink-muted hover:text-ink hover:bg-field data-[active=true]:border-accent data-[active=true]:bg-accent data-[active=true]:text-accent-ink"
          >
            {clearAppleKey ? "Clear Apple key ✓" : "Clear saved Apple key"}
          </button>
        </div>
      </div>

      <p
        data-testid="admin-settings-auth-restart-hint"
        className="rounded border border-rule bg-canvas px-2 py-1.5 text-[0.65rem] text-ink-muted"
      >
        {status.restart_hint}
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="submit"
          disabled={save.isPending}
          data-testid="admin-settings-auth-providers-save"
          className="rounded border border-accent bg-accent text-accent-ink hover:bg-accent-hover px-2 py-1 text-[0.65rem] font-display uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {save.isPending ? "Saving…" : "Save auth providers"}
        </button>
        {submitMessage ? (
          <span
            data-testid="admin-settings-auth-providers-save-status"
            className="text-[0.65rem] font-mono text-good"
          >
            {submitMessage}
          </span>
        ) : null}
        {submitError ? (
          <span
            data-testid="admin-settings-auth-providers-save-error"
            className="text-[0.65rem] font-mono text-destructive"
          >
            {submitError}
          </span>
        ) : null}
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// H1 — Branding card (+ H6 partial-save flow)
// ---------------------------------------------------------------------------

function BrandingCard({ settings }: { settings: AdminSettingsPayload }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const live = (settings.settings ?? {}) as Record<string, unknown>;
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const dirty = useMemo(
    () =>
      Object.keys(draft).filter(
        (k) => draft[k] !== (live[k] ?? "") && draft[k] !== undefined,
      ),
    [draft, live],
  );

  const save = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      updateAdminSettings(payload),
    onSuccess: async () => {
      // Invalidate both the admin payload AND the public app-settings
      // cache so the public shell/header picks up branding changes
      // (e.g. `season_title`) immediately instead of waiting for the
      // 30 s staleTime to expire. Mirrors the wizard's invalidation
      // flow in WelcomeShell.TeamStep.
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: unscopedKey("admin-settings"),
        }),
        queryClient.invalidateQueries({
          queryKey: unscopedKey("app-settings"),
        }),
      ]);
      setDraft({});
      setFieldErrors({});
      toast.success("Settings saved");
    },
    onError: (err) => {
      toast.error("Couldn't save settings", { description: formatErr(err) });
      if (err instanceof ApiError && err.status === 400) {
        // Per-key 400 from the backend's `errors` map.
        const detail = err.detail as unknown;
        if (
          detail &&
          typeof detail === "object" &&
          "errors" in (detail as Record<string, unknown>)
        ) {
          const errs = (detail as { errors: Record<string, string> }).errors;
          setFieldErrors(errs);
          setSubmitError("Some fields failed validation.");
          return;
        }
      }
      setSubmitError(formatErr(err));
    },
  });

  const grouped = useMemo(() => {
    const out: Record<string, typeof BRANDING_FIELDS> = {};
    for (const f of BRANDING_FIELDS) {
      (out[f.group] ||= []).push(f);
    }
    return out;
  }, []);

  return (
    <section
      data-testid="admin-settings-branding"
      className="flex flex-col gap-3 rounded border border-rule bg-surface shadow-sm p-3"
    >
      <header className="flex items-baseline justify-between flex-wrap gap-2">
        <h3 className="text-sm font-display uppercase tracking-wider text-ink">
          Branding
        </h3>
        <button
          type="button"
          onClick={() => {
            setSubmitError(null);
            setFieldErrors({});
            const payload = Object.fromEntries(dirty.map((k) => [k, draft[k]]));
            save.mutate(payload);
          }}
          disabled={dirty.length === 0 || save.isPending}
          data-testid="admin-settings-branding-save"
          className="rounded border border-accent bg-accent text-accent-ink hover:bg-accent-hover px-2 py-0.5 text-[0.65rem] font-display uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {save.isPending
            ? "Saving…"
            : `Save${dirty.length ? ` (${dirty.length})` : ""}`}
        </button>
      </header>

      {submitError ? (
        <p
          data-testid="admin-settings-branding-error"
          className="text-destructive font-mono text-xs"
        >
          {submitError}
        </p>
      ) : null}

      {Object.entries(grouped).map(([group, fields]) => (
        <div key={group} className="flex flex-col gap-2">
          <h4 className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
            {BRANDING_GROUP_LABELS[group as keyof typeof BRANDING_GROUP_LABELS]}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {fields.map((f) => (
              <BrandingField
                key={f.key}
                field={f}
                currentValue={
                  draft[f.key] !== undefined
                    ? draft[f.key]
                    : String(live[f.key] ?? "")
                }
                originalValue={String(live[f.key] ?? "")}
                error={fieldErrors[f.key]}
                onChange={(v) =>
                  setDraft((d) => ({ ...d, [f.key]: v }))
                }
              />
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}

function BrandingField({
  field,
  currentValue,
  originalValue,
  error,
  onChange,
}: {
  field: (typeof BRANDING_FIELDS)[number];
  currentValue: string;
  originalValue: string;
  error: string | undefined;
  onChange: (v: string) => void;
}) {
  const dirty = currentValue !== originalValue;
  return (
    <label
      className="flex flex-col gap-1 text-xs"
      data-testid={`admin-settings-field-${field.key}`}
      data-dirty={dirty ? "true" : undefined}
    >
      <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
        {field.label}
      </span>
      {field.kind === "textarea" ? (
        <textarea
          value={currentValue}
          onChange={(e) => onChange(e.target.value)}
          rows={2}
          data-testid={`admin-settings-input-${field.key}`}
          className="rounded border border-rule bg-surface px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-accent"
        />
      ) : (
        <input
          type="text"
          value={currentValue}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`admin-settings-input-${field.key}`}
          className="rounded border border-rule bg-surface px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-accent"
        />
      )}
      {error ? (
        <span
          data-testid={`admin-settings-error-${field.key}`}
          className="text-destructive text-[0.6rem] font-mono"
        >
          {error}
        </span>
      ) : null}
    </label>
  );
}

// ---------------------------------------------------------------------------
// H3 — Downloads toggle
// ---------------------------------------------------------------------------

function DownloadsCard({ settings }: { settings: AdminSettingsPayload }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const live = (settings.settings ?? {}) as Record<string, unknown>;
  const currentEnabled = coerceBool(live.downloads_enabled);
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: (next: boolean) =>
      updateAdminSettings({ downloads_enabled: next ? "1" : "0" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: unscopedKey("admin-settings"),
      });
      toast.success("Settings saved");
    },
    onError: (err) => {
      setError(formatErr(err));
      toast.error("Couldn't save settings", { description: formatErr(err) });
    },
  });

  return (
    <section
      data-testid="admin-settings-downloads"
      className="flex flex-col gap-2 rounded border border-rule bg-surface shadow-sm p-3"
    >
      <h3 className="text-sm font-display uppercase tracking-wider text-ink">
        Downloads
      </h3>
      <button
        type="button"
        onClick={() => {
          setError(null);
          mut.mutate(!currentEnabled);
        }}
        disabled={mut.isPending}
        data-testid="admin-settings-downloads-toggle"
        data-active={currentEnabled ? "true" : undefined}
        className="self-start rounded border px-3 py-1.5 text-xs font-display uppercase tracking-wider transition-colors border-rule text-ink-muted hover:text-ink hover:bg-field data-[active=true]:border-accent data-[active=true]:bg-accent data-[active=true]:text-accent-ink disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {currentEnabled ? "Downloads enabled" : "Downloads disabled"}
      </button>
      {error ? (
        <p
          data-testid="admin-settings-downloads-error"
          className="text-destructive font-mono text-xs"
        >
          {error}
        </p>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// H4 — Asset uploads
// ---------------------------------------------------------------------------

function AssetsCard({ settings }: { settings: AdminSettingsPayload }) {
  return (
    <section
      data-testid="admin-settings-assets"
      className="flex flex-col gap-2 rounded border border-rule bg-surface shadow-sm p-3"
    >
      <h3 className="text-sm font-display uppercase tracking-wider text-ink">
        App assets
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <AssetRow
          kind="club_logo"
          label="Club logo"
          previewUrl={settings.assets?.club_logo_url ?? null}
        />
        <AssetRow
          kind="favicon"
          label="Favicon"
          previewUrl={settings.assets?.favicon_url ?? null}
        />
      </div>
    </section>
  );
}

function AssetRow({
  kind,
  label,
  previewUrl,
}: {
  kind: "club_logo" | "favicon";
  label: string;
  previewUrl: string | null;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: (file: File) => uploadAdminSettingsAsset(kind, file),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: unscopedKey("admin-settings"),
      });
      toast.success(`${label} uploaded`);
    },
    onError: (err) => {
      setError(formatErr(err));
      toast.error(`Couldn't upload ${label.toLowerCase()}`, {
        description: formatErr(err),
      });
    },
  });

  return (
    <div
      data-testid={`admin-settings-asset-${kind}`}
      className="flex flex-col gap-2 rounded border border-rule bg-elevated p-2"
    >
      <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
        {label}
      </span>
      <div className="flex items-center gap-2">
        {previewUrl ? (
          <img
            src={previewUrl}
            alt={`Current ${label.toLowerCase()}`}
            data-testid={`admin-settings-asset-${kind}-preview`}
            className="h-10 w-10 object-contain rounded border border-rule bg-canvas"
          />
        ) : (
          <div
            data-testid={`admin-settings-asset-${kind}-no-preview`}
            className="h-10 w-10 rounded border border-dashed border-rule text-[0.55rem] flex items-center justify-center text-ink-muted"
          >
            none
          </div>
        )}
        <input
          type="file"
          accept={kind === "favicon" ? ".ico,.png,.svg" : ".png,.jpg,.jpeg,.svg,.webp"}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            setError(null);
            mut.mutate(file);
            // Reset the input so picking the same file twice still
            // fires onChange.
            e.target.value = "";
          }}
          disabled={mut.isPending}
          data-testid={`admin-settings-asset-${kind}-input`}
          className="text-xs text-ink-muted file:rounded file:border file:border-rule file:bg-surface file:text-ink file:px-2 file:py-0.5 file:text-[0.6rem] file:font-display file:uppercase file:tracking-wider"
        />
      </div>
      {mut.isPending ? (
        <p className="text-[0.6rem] font-mono text-ink-muted">Uploading…</p>
      ) : null}
      {error ? (
        <p
          data-testid={`admin-settings-asset-${kind}-error`}
          className="text-destructive font-mono text-[0.6rem]"
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// H5 / PR E — Email / Notification card (editable + test-send)
// ---------------------------------------------------------------------------

function EmailCard() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: unscopedKey("admin-email-settings"),
    queryFn: getAdminEmailSettings,
  });

  return (
    <section
      data-testid="admin-settings-email"
      className="flex flex-col gap-3 rounded border border-rule bg-surface shadow-sm p-3"
    >
      <header className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-sm font-display uppercase tracking-wider text-ink">
            Email / Notifications
          </h3>
          <p className="text-[0.65rem] text-ink-muted">
            Configure Brevo delivery and send a one-off test without exposing the stored API key.
          </p>
        </div>
      </header>
      {query.isLoading ? (
        <p className="text-[0.6rem] font-mono text-ink-muted">Loading…</p>
      ) : query.error ? (
        <p
          data-testid="admin-settings-email-error"
          className="text-destructive font-mono text-xs"
        >
          {formatErr(query.error)}
        </p>
      ) : query.data ? (
        <>
          <EmailSettingsForm
            payload={query.data}
            onSaved={async () => {
              await queryClient.invalidateQueries({
                queryKey: unscopedKey("admin-email-settings"),
              });
            }}
          />
          <EmailTestForm />
        </>
      ) : null}
    </section>
  );
}

function emailDefaults(payload: AdminEmailPayload): EmailSettingsValues {
  return {
    email_provider: payload.settings.email_provider === "brevo" ? "brevo" : "disabled",
    email_public_base_url: payload.settings.email_public_base_url ?? "",
    email_from: payload.settings.email_from ?? "",
    email_from_name: payload.settings.email_from_name ?? "Tktic",
    email_brevo_api_key: "",
    clear_brevo_api_key: false,
  };
}

function EmailSettingsForm({
  payload,
  onSaved,
}: {
  payload: AdminEmailPayload;
  onSaved: () => Promise<void>;
}) {
  const toast = useToast();
  const [submitMessage, setSubmitMessage] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const form = useForm<EmailSettingsValues>({
    resolver: zodResolver(emailSettingsSchema),
    values: emailDefaults(payload),
  });
  const clearKey = form.watch("clear_brevo_api_key");
  const { settings, status, provider, configured } = payload;

  const save = useMutation({
    mutationFn: updateAdminEmailSettings,
    onSuccess: async (next) => {
      form.reset(emailDefaults(next));
      setSubmitError(null);
      setSubmitMessage("Email settings saved.");
      toast.success("Email settings saved");
      await onSaved();
    },
    onError: (err) => {
      setSubmitMessage(null);
      setSubmitError(formatErr(err));
      toast.error("Couldn't save email settings", {
        description: formatErr(err),
      });
    },
  });

  const onSubmit: SubmitHandler<EmailSettingsValues> = (values) => {
    setSubmitError(null);
    setSubmitMessage(null);
    const body = {
      email_provider: values.email_provider,
      email_public_base_url: values.email_public_base_url.trim(),
      email_from: values.email_from.trim(),
      email_from_name: values.email_from_name.trim(),
      ...(values.clear_brevo_api_key
        ? { clear_brevo_api_key: true }
        : values.email_brevo_api_key.trim()
          ? { email_brevo_api_key: values.email_brevo_api_key.trim() }
          : {}),
    };
    save.mutate(body);
  };

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      data-testid="admin-settings-email-form"
      className="flex flex-col gap-3 text-xs"
    >
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <SettingsStatusPill
          label="Provider"
          value={provider}
          sub={`source: ${status.provider_source}`}
          testid="admin-settings-email-provider"
        />
        <SettingsStatusPill
          label="Delivery"
          value={configured ? "Ready" : "Needs setup"}
          tone={configured ? "good" : "warn"}
          testid="admin-settings-email-configured"
        />
        <SettingsStatusPill
          label="Sender"
          value={status.has_from ? "Set" : "Missing"}
          tone={status.has_from ? "muted" : "warn"}
        />
        <SettingsStatusPill
          label="API key"
          value={status.has_brevo_api_key ? "Configured" : "Not set"}
          sub={
            status.has_brevo_api_key
              ? `source: ${status.brevo_api_key_source}`
              : undefined
          }
          tone={status.has_brevo_api_key ? "good" : "warn"}
          testid="admin-settings-email-brevo-key"
        />
      </div>

      <div className="rounded border border-rule bg-elevated">
        <div className="grid grid-cols-1 divide-y divide-rule md:grid-cols-2 md:divide-x md:divide-y-0">
          <InlineEmailSelect
            label="App provider"
            current={`Current: ${provider}`}
            control={form.control}
          />
          <InlineSettingsField
            label="Public base URL"
            current={settings.email_public_base_url || "Not set"}
            currentTestid="admin-settings-email-base-url"
            testid="admin-settings-email-base-url-input"
            registration={form.register("email_public_base_url")}
            error={form.formState.errors.email_public_base_url?.message}
            placeholder="https://tktic.com"
          />
        </div>
        <div className="grid grid-cols-1 divide-y divide-rule border-t border-rule md:grid-cols-2 md:divide-x md:divide-y-0">
          <InlineSettingsField
            label="From address"
            current={settings.email_from || "Not set"}
            currentTestid="admin-settings-email-from"
            testid="admin-settings-email-from-input"
            registration={form.register("email_from")}
            error={form.formState.errors.email_from?.message}
            placeholder="noreply@tktic.com"
            type="email"
          />
          <InlineSettingsField
            label="From name"
            current={settings.email_from_name || "Not set"}
            currentTestid="admin-settings-email-from-name"
            testid="admin-settings-email-from-name-input"
            registration={form.register("email_from_name")}
            error={form.formState.errors.email_from_name?.message}
            placeholder="Tktic"
          />
        </div>
        <div className="grid grid-cols-1 divide-y divide-rule border-t border-rule md:grid-cols-[1fr_auto] md:divide-x md:divide-y-0">
          <InlineSettingsField
            label="New Brevo API key"
            current={
              status.has_brevo_api_key
                ? `Configured from ${status.brevo_api_key_source}`
                : "Not set"
            }
            testid="admin-settings-email-brevo-key-input"
            registration={form.register("email_brevo_api_key")}
            error={form.formState.errors.email_brevo_api_key?.message}
            placeholder={status.has_brevo_api_key ? "Leave blank to keep current key" : "Paste new Brevo API key"}
            type="password"
            autoComplete="new-password"
            disabled={clearKey}
          />
          <div className="flex items-center p-2">
            <button
              type="button"
              onClick={() => form.setValue("clear_brevo_api_key", !clearKey)}
              data-testid="admin-settings-email-clear-key"
              data-active={clearKey ? "true" : undefined}
              className="w-full rounded border px-3 py-2 text-xs font-display uppercase tracking-wider transition-colors border-rule text-ink-muted hover:text-ink hover:bg-field data-[active=true]:border-accent data-[active=true]:bg-accent data-[active=true]:text-accent-ink md:w-auto"
            >
              {clearKey ? "Clear Brevo key ✓" : "Clear saved key"}
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="submit"
          disabled={save.isPending}
          data-testid="admin-settings-email-save"
          className="rounded border border-accent bg-accent text-accent-ink hover:bg-accent-hover px-2 py-1 text-[0.65rem] font-display uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {save.isPending ? "Saving…" : "Save email settings"}
        </button>
        {submitMessage ? (
          <span data-testid="admin-settings-email-save-status" className="text-[0.65rem] font-mono text-good">
            {submitMessage}
          </span>
        ) : null}
        {submitError ? (
          <span data-testid="admin-settings-email-save-error" className="text-[0.65rem] font-mono text-destructive">
            {submitError}
          </span>
        ) : null}
      </div>
    </form>
  );
}

function SettingsStatusPill({
  label,
  value,
  sub,
  tone = "muted",
  testid,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: "muted" | "good" | "warn";
  testid?: string;
}) {
  const toneClass =
    tone === "good"
      ? "text-good"
      : tone === "warn"
        ? "text-accent"
        : "text-ink";
  return (
    <div
      data-testid={testid}
      className="rounded border border-rule bg-elevated px-2 py-1.5"
    >
      <span className="block text-[0.55rem] font-display uppercase tracking-wider text-ink-muted">
        {label}
      </span>
      <span className={`block text-xs font-mono ${toneClass}`}>{value}</span>
      {sub ? (
        <span className="block truncate text-[0.55rem] font-mono text-ink-muted">
          {sub}
        </span>
      ) : null}
    </div>
  );
}

function sourceLabel(source: string): string | undefined {
  return source ? `source: ${source}` : undefined;
}

function InlineEmailSelect({
  label,
  current,
  control,
}: {
  label: string;
  current: string;
  control: Control<EmailSettingsValues>;
}) {
  return (
    <label className="grid gap-2 p-2 md:grid-cols-[minmax(9rem,0.7fr)_minmax(12rem,1fr)] md:items-center">
      <span>
        <span className="block text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
          {label}
        </span>
        <span className="block text-[0.6rem] font-mono text-ink-muted">
          {current}
        </span>
      </span>
      <Controller
        control={control}
        name="email_provider"
        render={({ field }) => (
          <TkticSelect
            value={field.value ?? "disabled"}
            onChange={field.onChange}
            data-testid="admin-settings-email-provider-input"
            ariaLabel="Email provider"
            size="sm"
            options={[
              { value: "disabled", label: "Disabled" },
              { value: "brevo", label: "Brevo" },
            ]}
          />
        )}
      />
    </label>
  );
}

function InlineSettingsField({
  label,
  current,
  currentTestid,
  testid,
  registration,
  error,
  placeholder,
  type = "text",
  autoComplete,
  inputMode,
  disabled,
}: {
  label: string;
  current: string;
  currentTestid?: string;
  testid: string;
  registration: UseFormRegisterReturn;
  error?: string;
  placeholder?: string;
  type?: string;
  autoComplete?: string;
  inputMode?: InputHTMLAttributes<HTMLInputElement>["inputMode"];
  disabled?: boolean;
}) {
  return (
    <label className="grid gap-2 p-2 md:grid-cols-[minmax(9rem,0.7fr)_minmax(12rem,1fr)] md:items-start">
      <span>
        <span className="block text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
          {label}
        </span>
        <span
          data-testid={currentTestid}
          className="block break-words text-[0.6rem] font-mono text-ink-muted"
        >
          Current: {current}
        </span>
      </span>
      <span className="flex flex-col gap-1">
        <input
          type={type}
          {...registration}
          data-testid={testid}
          placeholder={placeholder}
          autoComplete={autoComplete}
          inputMode={inputMode}
          disabled={disabled}
          className="rounded border border-rule bg-surface px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50 disabled:cursor-not-allowed"
        />
        {error ? <span className="text-destructive text-[0.6rem] font-mono">{error}</span> : null}
      </span>
    </label>
  );
}

function InlineSettingsTextArea({
  label,
  current,
  currentTestid,
  testid,
  registration,
  error,
  placeholder,
  disabled,
}: {
  label: string;
  current: string;
  currentTestid?: string;
  testid: string;
  registration: UseFormRegisterReturn;
  error?: string;
  placeholder?: string;
  disabled?: boolean;
}) {
  return (
    <label className="grid gap-2 p-2 md:grid-cols-[minmax(9rem,0.7fr)_minmax(12rem,1fr)] md:items-start">
      <span>
        <span className="block text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
          {label}
        </span>
        <span
          data-testid={currentTestid}
          className="block break-words text-[0.6rem] font-mono text-ink-muted"
        >
          Current: {current}
        </span>
      </span>
      <span className="flex flex-col gap-1">
        <textarea
          {...registration}
          data-testid={testid}
          placeholder={placeholder}
          disabled={disabled}
          rows={5}
          className="min-h-24 rounded border border-rule bg-surface px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50 disabled:cursor-not-allowed"
        />
        {error ? <span className="text-destructive text-[0.6rem] font-mono">{error}</span> : null}
      </span>
    </label>
  );
}

function EmailTestForm() {
  const toast = useToast();
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const form = useForm<EmailTestValues>({
    resolver: zodResolver(emailTestSchema),
    defaultValues: { email: "" },
  });
  const sendTest = useMutation({
    mutationFn: sendAdminEmailTest,
    onSuccess: (response) => {
      setError(null);
      setResult(
        response.ok
          ? `Test email sent${response.message_id ? ` · ${response.message_id}` : ""}`
          : `Test email ${response.status}: ${response.detail || response.provider}`,
      );
      if (response.ok) {
        toast.success("Test email sent");
      } else {
        toast.error("Couldn't send test email", {
          description: `${response.status}: ${response.detail || response.provider}`,
        });
      }
    },
    onError: (err) => {
      setResult(null);
      setError(formatErr(err));
      toast.error("Couldn't send test email", { description: formatErr(err) });
    },
  });

  const onSubmit: SubmitHandler<EmailTestValues> = (values) => {
    setResult(null);
    setError(null);
    sendTest.mutate({ email: values.email.trim() });
  };

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      data-testid="admin-settings-email-test-form"
      className="flex flex-col gap-2 rounded border border-rule bg-elevated p-2 text-xs"
    >
      <label className="flex flex-col gap-1">
        <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
          Send test email
        </span>
        <input
          type="email"
          {...form.register("email")}
          data-testid="admin-settings-email-test-input"
          placeholder="operator@example.test"
          className="rounded border border-rule bg-canvas px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-accent"
        />
        {form.formState.errors.email?.message ? (
          <span className="text-destructive text-[0.6rem] font-mono">
            {form.formState.errors.email.message}
          </span>
        ) : null}
      </label>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="submit"
          disabled={sendTest.isPending}
          data-testid="admin-settings-email-test-send"
          className="rounded border border-accent bg-accent text-accent-ink hover:bg-accent-hover px-2 py-1 text-[0.65rem] font-display uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {sendTest.isPending ? "Sending…" : "Send test"}
        </button>
        {result ? (
          <span data-testid="admin-settings-email-test-result" className="text-[0.65rem] font-mono text-good">
            {result}
          </span>
        ) : null}
        {error ? (
          <span data-testid="admin-settings-email-test-error" className="text-[0.65rem] font-mono text-destructive">
            {error}
          </span>
        ) : null}
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// H7 — Audit feed (controlled expander)
// ---------------------------------------------------------------------------

function AuditCard({
  audit,
}: {
  audit: NonNullable<AdminSettingsPayload["audit"]>;
}) {
  const [open, setOpen] = useState(false);
  return (
    <section
      data-testid="admin-settings-audit"
      data-expander-open={open ? "true" : "false"}
      className="rounded border border-rule bg-surface shadow-sm"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid="admin-settings-audit-toggle"
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-sm"
      >
        <span className="font-display uppercase tracking-wider text-ink">
          Settings audit
        </span>
        <span className="text-[0.6rem] font-mono text-ink-muted">
          {audit.length} {open ? "▼" : "▶"}
        </span>
      </button>
      {open ? (
        <div className="px-3 pb-3">
          {audit.length === 0 ? (
            <p
              data-testid="admin-settings-audit-empty"
              className="text-[0.6rem] font-mono text-ink-muted"
            >
              No settings changes recorded.
            </p>
          ) : (
            <ul className="flex flex-col gap-1">
              {audit.map((a, i) => (
                <li
                  key={`${a.timestamp}-${a.key}-${i}`}
                  data-testid="admin-settings-audit-row"
                  className="rounded border border-rule/60 bg-elevated px-2 py-1 text-[0.6rem] font-mono"
                >
                  <span className="text-ink-muted">{a.timestamp}</span>{" "}
                  <span className="text-ink">{a.key}</span>{" "}
                  <span className="text-ink-muted">by {a.actor}</span>{" "}
                  <span className="text-ink-muted">
                    · {JSON.stringify(a.old_value)} → {JSON.stringify(a.new_value)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

// coerceBool moved to `web/src/lib/coerce.ts` (M11 lift — public viewer + admin share).

function formatErr(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.detail && typeof err.detail === "object" && "message" in err.detail) {
      const msg = (err.detail as { message?: unknown }).message;
      if (typeof msg === "string") return msg;
    }
    return typeof err.detail === "string" ? err.detail : `HTTP ${err.status}`;
  }
  return "Request failed";
}
